import { UserService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/user_connect";
import type { CallOptions } from "@connectrpc/connect";
import { createConnectClient } from "../../connect";
import { mapAdminUser } from "./mappers";

export const SYSTEM_SUPERUSER_ROLE_ID = "role:system:superuser";
const adminUserPageSizes = new Set([10, 25, 50, 100]);
const newestAdminUsersFirst = {
  orderBy: "create_time",
  ordering: "desc" as const,
};
const userClient = createConnectClient(UserService);

const adminUserAttachedDataOptions = { emails: true, externalIdentities: true };
const adminUserPageAttachedDataOptions = { emails: true };

export interface ListAdminUsersPageInput {
  pageSize: number;
  pageToken?: string;
  email?: string;
  externalId?: string;
  username?: string;
}

export interface AdminUserPage {
  items: ReturnType<typeof mapAdminUser>[];
  nextPageToken: string;
}

export async function getAdminUser(userId: string, options?: CallOptions) {
  const response = await userClient.getUser(
    { userId, attachedDataOptions: adminUserAttachedDataOptions },
    options,
  );
  if (!response.user) {
    throw new Error("user was not returned");
  }
  return mapAdminUser(response.user);
}

export async function findAdminUsersByEmail(
  email: string,
  options?: CallOptions,
) {
  const response = await userClient.listUser(
    {
      options: {
        emails: [email],
        orderOptions: newestAdminUsersFirst,
        attachedDataOptions: adminUserAttachedDataOptions,
      },
    },
    options,
  );
  return response.items.map(mapAdminUser);
}

export async function listAdminUsersPage(
  input: ListAdminUsersPageInput,
  options?: CallOptions,
): Promise<AdminUserPage> {
  if (!adminUserPageSizes.has(input.pageSize)) {
    throw new RangeError("admin user page size must be 10, 25, 50, or 100");
  }

  const response = await userClient.listUser(
    {
      pageParams: {
        pageSize: input.pageSize,
        pageToken: input.pageToken ?? "",
      },
      options: {
        orderOptions: newestAdminUsersFirst,
        usernames: input.username ? [input.username] : [],
        emails: input.email ? [input.email] : [],
        externalIds: input.externalId ? [input.externalId] : [],
        attachedDataOptions: adminUserPageAttachedDataOptions,
      },
    },
    options,
  );

  return {
    items: response.items.map(mapAdminUser),
    nextPageToken: response.nextPageToken,
  };
}

export async function getCurrentUser(options?: CallOptions) {
  const response = options
    ? await userClient.getCurrentUser({}, options)
    : await userClient.getCurrentUser({});
  return response.user ?? null;
}

export async function getCurrentUserAdminAccess(options?: CallOptions) {
  const user = await getCurrentUser(options);
  return (
    user?.roles.some((role) => role.roleId === SYSTEM_SUPERUSER_ROLE_ID) ??
    false
  );
}

export async function removeOrganizationMember(
  organizationId: string,
  userId: string,
) {
  await userClient.removeOrganizationAccessForUser({ organizationId, userId });
}
